import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, ImageUp, X, Pencil } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { compressImage, dataUrlSizeKB } from "@/lib/image";

const RARITIES = ["common", "uncommon", "rare", "epic", "legendary", "goddess"];
const DEFAULT_ODDS = { common: 60, uncommon: 25, rare: 10, epic: 4, legendary: 0.9, goddess: 0.1 };
const INITIAL_FORM = { name: "", description: "", price_credits: 250, price_sp: "", odds: { ...DEFAULT_ODDS }, image: null, cards_per_pack: 1 };

export default function AdminPacks() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);

  const { data: packs } = useQuery({
    queryKey: ["admin-packs"],
    queryFn: async () => (await api.get("/admin/packs")).data,
  });

  const openNew = () => { setEditingId(null); setForm(INITIAL_FORM); setOpen(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name || "",
      description: p.description || "",
      price_credits: p.price_credits ?? 0,
      price_sp: p.price_sp ?? "",
      odds: RARITIES.reduce((o, r) => ({ ...o, [r]: +(((p.odds?.[r] ?? 0) * 100).toFixed(2)) }), {}),
      image: p.image || null,
      cards_per_pack: p.cards_per_pack || 1,
    });
    setOpen(true);
  };

  const save = useMutation({
    mutationFn: async () => {
      const odds = Object.fromEntries(Object.entries(form.odds).map(([k, v]) => [k, Number(v) / 100]));
      const payload = { ...form, odds, price_sp: form.price_sp === "" || form.price_sp == null ? null : Number(form.price_sp) };
      if (editingId) return (await api.put(`/admin/packs/${editingId}`, payload)).data;
      return (await api.post("/admin/packs", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-packs"] });
      setOpen(false);
      setEditingId(null);
      setForm(INITIAL_FORM);
      toast.success(editingId ? "Pack updated" : "Pack created");
    },
    onError: (e) => toast.error(apiError(e, "Failed")),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/packs/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-packs"] }); toast.success("Deleted"); },
  });

  const total = Object.values(form.odds).reduce((a, b) => a + Number(b), 0);

  const onImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const dataUrl = await compressImage(f, { maxSize: 1280, quality: 0.86 });
      setForm((prev) => ({ ...prev, image: dataUrl }));
      toast.success(`Cover added (${dataUrlSizeKB(dataUrl)} KB)`);
    } catch {
      toast.error("Could not read that image");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-white tracking-[0.06em]">PACKS</h1>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(INITIAL_FORM); } }}>
          <Button data-testid="admin-new-pack-button" onClick={openNew} className="bg-cyan-400 text-black hover:bg-cyan-300">
            <Plus className="h-4 w-4 mr-1" /> New Pack
          </Button>
          <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10 max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl tracking-[0.08em]">{editingId ? "EDIT PACK" : "NEW PACK"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Name</Label>
                <Input data-testid="pack-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Description</Label>
                <Textarea data-testid="pack-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Price (credits)</Label>
                  <Input data-testid="pack-price-input" type="number" min={0} value={form.price_credits} onChange={(e) => setForm({ ...form, price_credits: Number(e.target.value) })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
                </div>
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Price (SP)</Label>
                  <Input data-testid="pack-price-sp-input" type="number" min={0} placeholder="—" value={form.price_sp} onChange={(e) => setForm({ ...form, price_sp: e.target.value })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
                </div>
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Cards / Pack</Label>
                  <Input data-testid="pack-cards-per-pack-input" type="number" min={1} max={20} value={form.cards_per_pack} onChange={(e) => setForm({ ...form, cards_per_pack: Number(e.target.value) })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
                </div>
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Rarity Odds (% — will normalize)</Label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  {RARITIES.map((r) => (
                    <div key={r} className="flex items-center gap-2">
                      <span className={`rarity-${r} px-2 py-1 rounded font-mono text-[10px] uppercase tracking-[0.2em]`} style={{ color: "var(--label)", border: "1px solid var(--edge)", background: "rgba(0,0,0,0.4)" }}>{r}</span>
                      <Input type="number" step="0.1" min="0" value={form.odds[r]} onChange={(e) => setForm({ ...form, odds: { ...form.odds, [r]: e.target.value } })} className="bg-[hsl(220_18%_12%)] border-white/10 h-8" />
                    </div>
                  ))}
                </div>
                <div className="text-xs text-white/40 mt-2">Total: {total.toFixed(1)}% (auto-normalized to 100%)</div>
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Cover Image (shown on the store)</Label>
                <div className="mt-1.5 flex items-center gap-3">
                  <div className="relative h-20 w-32 shrink-0 rounded-lg overflow-hidden border border-white/10 bg-gradient-to-br from-cyan-900/40 via-violet-900/30 to-amber-900/30 flex items-center justify-center">
                    {form.image ? (
                      <>
                        <img src={form.image} alt="cover" className="absolute inset-0 w-full h-full object-cover" />
                        <button
                          type="button"
                          data-testid="pack-image-remove"
                          onClick={() => setForm({ ...form, image: null })}
                          className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-500/80"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </>
                    ) : (
                      <ImageUp className="h-6 w-6 text-white/30" />
                    )}
                  </div>
                  <label className="cursor-pointer">
                    <input data-testid="pack-image-input" type="file" accept="image/*" onChange={onImage} className="hidden" />
                    <span className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-xs text-white/80 hover:bg-white/5">
                      <ImageUp className="h-4 w-4" /> {form.image ? "Replace image" : "Upload cover"}
                    </span>
                  </label>
                </div>
              </div>
              <Button data-testid="pack-create-button" onClick={() => save.mutate()} disabled={!form.name || save.isPending} className="w-full bg-cyan-400 text-black hover:bg-cyan-300">{editingId ? "Save Changes" : "Create"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {(packs || []).map((p) => (
          <div key={p.id} className="hud-pill rounded-2xl p-4">
            {p.image && (
              <div className="relative -mx-4 -mt-4 mb-3 h-28 overflow-hidden rounded-t-2xl">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover" data-testid={`admin-pack-image-${p.id}`} />
                <div className="absolute inset-0 holo-scan opacity-30" />
              </div>
            )}
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display text-xl text-white tracking-[0.06em]">{p.name}</div>
                <div className="text-xs text-white/50 mt-1">{p.description}</div>
                <div className="text-amber-300 font-mono mt-2 flex items-center gap-3 flex-wrap">
                  <span>{p.price_credits.toLocaleString()} credits</span>
                  {p.price_sp ? <span className="text-yellow-200" data-testid={`admin-pack-sp-${p.id}`}>· {p.price_sp} SP</span> : null}
                  <span className="text-cyan-300/80">· {p.cards_per_pack || 1} card{(p.cards_per_pack || 1) === 1 ? "" : "s"} per pack</span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="sm" data-testid={`admin-pack-edit-${p.id}`} onClick={() => openEdit(p)} className="text-cyan-300"><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="sm" data-testid={`admin-pack-delete-${p.id}`} onClick={() => { if (window.confirm("Delete pack?")) del.mutate(p.id); }} className="text-red-400"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-1 mt-3">
              {Object.entries(p.odds || {}).map(([r, v]) => (
                <div key={r} className={`rarity-${r} text-center px-1.5 py-1 rounded`} style={{ background: "rgba(0,0,0,0.4)", border: "1px solid var(--edge)" }}>
                  <div className="font-mono text-[8px] uppercase tracking-[0.2em]" style={{ color: "var(--label)" }}>{r}</div>
                  <div className="font-mono text-[10px] text-white/70">{(v * 100).toFixed(1)}%</div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {(packs || []).length === 0 && <div className="col-span-full text-center text-white/40 py-12">No packs yet.</div>}
      </div>
    </div>
  );
}
